<script setup lang="ts">
import InterviewInstanceModalHeader from "@/interview/components/instance-modal/InterviewInstanceModalHeader.vue";
import {computed, onUnmounted, provide, ref, watch} from "vue";
import {useInterviewStore} from "@/stores/interview-store";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";
import InterviewShortCuts from "@/interview/components/instance-modal/InterviewShortCuts.vue";
import {useRoute, useRouter} from "vue-router";
import {usePatientStore} from "@/stores/patient.store";
import {storeToRefs} from "pinia";
import {InterviewInstance} from "@/interview/interfaces/interview";

const interviewStore = useInterviewStore();
const router = useRouter();
const route = useRoute();
const patientStore = usePatientStore();
const {interviewInstance, consumerName, interview} = storeToRefs(interviewStore);
const shortcuts = ref();

const customerId = computed(() => route.query.customerId as string);
const consumerKey = computed(() => route.query.consumerKey as string);
const interviewId = computed(() => route.query.interviewId as string);
const interviewName = computed(() => route.query.interviewName as string);
const backingId = computed(() => route.query.backingId as string | undefined);
const interviewInstanceId = computed(() => route.query.interviewInstanceId as string | undefined);

/**
 * Watch the interview id and initialize the interview store accordingly.
 */
watch(interviewId, async (newId) => {
    await interviewStore.initialize(
        Number(newId),
        Number(customerId.value),
        consumerKey.value,
        Number(interviewInstanceId.value),
        backingId.value ? Number(backingId.value) : undefined
    );

    const routeName = getInterviewRedirectState(interviewInstance.value);

    if (routeName !== route.name) {
        return await router.replace({
            name: routeName,
            query: {...route.query}
        });
    }
}, {immediate: true});

/**
 * On unmounted, reset the interview store and refresh the patient store.
 */
onUnmounted(() => {
    interviewStore.$reset();
    patientStore.refresh();
});

/**
 * Determine if the store is loading before rendering any content.
 */
const storeIsLoading = computed(() => {
    return interviewStore.loading.interview
        || interviewStore.loading.attachments
        || interviewStore.loading.interviewInstance
        || interviewStore.loading.customer;
});

/**
 * Handle the close event.
 */
const handleClose = () => {
    router.back();
};

/**
 * Get the interview instance redirect state.
 */
const getInterviewRedirectState = (instance?: InterviewInstance | null): string => {
    if (!instance) {
        return interview.value?.guideline_survey ? 'interview-instances.guideline' : 'interview-instances.survey';
    }

    return 'interview-instances.finished';
};

provide('close', handleClose);
provide(
    'interviewShortcuts',
    {
        openDrugsInteraction: () => shortcuts.value.openDrugsInteraction()
    }
);
</script>

<template>
  <div class="flex-1 flex flex-col h-full">
    <InterviewInstanceModalHeader
      :consumer-name="consumerName"
      :interview-name="interviewName"
      @close="handleClose"
    />
    <BaseContainer class="flex-1 bg-gray-50 flex flex-col">
      <dot-lottie-vue
        v-if="storeIsLoading"
        src="animations/interview.json"
        class="mx-auto my-auto w-100"
        :autoplay="true"
        :loop="true"
      />
      <div v-else-if="interviewStore.interview" class="flex flex-1 flex-col">
        <div class="flex flex-1 gap-6">
          <router-view :key="$route.path" v-slot="{Component}" class="flex-1 flex">
            <component :is="Component">
              <template #shortcuts>
                <InterviewShortCuts ref="shortcuts" />
              </template>
            </component>
          </router-view>
        </div>
      </div>
    </BaseContainer>
  </div>
</template>

<style scoped>

</style>
